/**
 * La identidad de la casa como la ve el equipo: METADATOS, nunca nombres de
 * personas (§5). El nombre sale de la API (`companyName`); si la casa aún no
 * tiene fila estructural, el slug humanizado — que es la verdad disponible,
 * no un invento. Los directivos se cuentan, no se nombran.
 */
import type { EngagementSummary } from "@skein/contracts";
import { fechaLarga } from "./fecha";

/** El id de una casa real es un UUID: se cita por su cabeza, como los
 *  episodios. El slug de una casa del esqueleto va entero. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const idCorto = (engagementId: string): string => (UUID.test(engagementId) ? engagementId.slice(0, 8) : engagementId);

/** `eng-carnicas-illa` → `Carnicas Illa`. Último recurso, no adorno: un id
 *  de casa real es un UUID y no se deja «humanizar» — entonces se la nombra
 *  por su id corto, que dice la verdad («no sé cómo se llama, es esta»). */
export const humanizarSlug = (engagementId: string): string =>
  UUID.test(engagementId)
    ? `Casa ${idCorto(engagementId)}`
    : engagementId.replace(/^eng-/, "").split("-").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ") || engagementId;

export const nombreCasa = (engagementId: string, companyName?: string | null): string =>
  companyName?.trim() || humanizarSlug(engagementId);

/** «3 directivos · 1 administra» — recuento, jamás quiénes (§5). Quien está
 *  solo en su casa la administra por definición (es la fundadora, ADR-1):
 *  decirlo aparte alarga la fila sin añadir nada. */
export const recuentoDirectivos = (executives: number, admins: number): string => {
  if (executives <= 0) return "sin directivos";
  const cuantos = executives === 1 ? "1 directivo" : `${executives} directivos`;
  if (admins <= 0 || (executives === 1 && admins === 1)) return cuantos;
  return `${cuantos} · ${admins === 1 ? "1 administra" : `${admins} administran`}`;
};

/** El sabor es un enum del producto: en pantalla va su nombre, no el slug
 *  («Propuesta Aquila» en Preferencias es el mismo nombre). */
const SABOR: Record<string, string> = { aquila: "Aquila" };
export const saborCasa = (flavor: string | null): string | null => (flavor ? SABOR[flavor] ?? flavor : null);

/** La sublínea de la fila: id · sector · recuento. El SABOR no entra: hoy es
 *  el mismo en todas las casas, no distingue nada y parte la fila en dos
 *  líneas — vive en la cabecera del detalle, donde sí hay sitio. */
export const metaCasa = (e: EngagementSummary): string =>
  [idCorto(e.engagementId), e.sector, recuentoDirectivos(e.executives, e.admins)]
    .filter((p): p is string => Boolean(p)).join(" · ");

/**
 * «Documentos de cifras» (ADR-22 pt 8): lo que el equipo ve de los
 * documentos de una casa son METADATOS — cuántos fijados, si hay borrador,
 * qué conceptos sirven y el último corte —; nunca nombres de fichero ni
 * cifras. Igual en una casa de cliente que en una de prueba (el inspector
 * de pre la ve entera por otro camino).
 */
export const lineaDocumentosCifras = (d: EngagementSummary["documentosCifras"]): string => {
  if (!d) return "ninguno";
  const partes = [
    d.fijados === 1 ? "1 fijado" : `${d.fijados} fijados`,
    `borrador ${d.borrador ? "sí" : "no"}`,
    d.conceptos.length ? `conceptos servidos: ${d.conceptos.join(", ")}` : "ningún concepto servido",
    // el corte es un día de la casa (AAAA-MM-DD): a mediodía, para que ninguna zona horaria lo mueva
    ...(d.ultimoCorte ? [`último corte ${fechaLarga(`${d.ultimoCorte}T12:00:00`)}`] : []),
  ];
  return partes.join(" · ");
};
